import React, { useState } from "react";
import PropTypes from "prop-types";

const StepFiles = ({
  formData,
  handleFileChange,
  handleInputChange,
  article,
}) => {
  const [dragOver, setDragOver] = useState(false);
  const [fileInfo, setFileInfo] = useState(null);

  // Formats de fichiers acceptés
  const ACCEPTED_FILE_TYPES = {
    ".doc": "application/msword",
    ".docx":
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ".odt": "application/vnd.oasis.opendocument.text",
    ".rtf": "application/rtf",
    ".txt": "text/plain",
  };

  // Taille maximale du fichier (10MB)
  const MAX_FILE_SIZE = 10 * 1024 * 1024;

  // Fonction pour formater la taille du fichier
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Fonction pour obtenir l'icône du fichier
  const getFileIcon = (filename) => {
    const extension = filename.toLowerCase().split(".").pop();
    switch (extension) {
      case "doc":
      case "docx":
        return "📄"; // Document Word
      case "odt":
        return "📝"; // Document OpenDocument
      case "rtf":
        return "📋"; // Document RTF
      case "txt":
        return "📃"; // Document texte
      default:
        return "📄";
    }
  };

  // Validation du fichier
  const validateFile = (file) => {
    const errors = [];

    // Vérifier la taille
    if (file.size > MAX_FILE_SIZE) {
      errors.push(
        `Le fichier est trop volumineux (${formatFileSize(
          file.size
        )}). Taille maximale : ${formatFileSize(MAX_FILE_SIZE)}`
      );
    }

    // Vérifier le type
    const isValidType =
      Object.values(ACCEPTED_FILE_TYPES).includes(file.type) ||
      Object.keys(ACCEPTED_FILE_TYPES).some((ext) =>
        file.name.toLowerCase().endsWith(ext)
      );

    if (!isValidType) {
      errors.push(
        "Type de fichier non supporté. Utilisez : .doc, .docx, .odt, .rtf, ou .txt"
      );
    }

    return errors;
  };

  // Gestionnaire de changement de fichier amélioré
  const handleFileChangeEnhanced = (e) => {
    const file = e.target.files[0];
    if (file) {
      const errors = validateFile(file);

      if (errors.length > 0) {
        alert("Erreur de fichier :\n" + errors.join("\n"));
        e.target.value = ""; // Réinitialiser l'input
        setFileInfo(null);
        return;
      }

      // Mettre à jour les informations du fichier
      setFileInfo({
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: new Date(file.lastModified).toLocaleString("fr-FR"),
      });

      // Appeler la fonction parent
      handleFileChange(e);

      console.log("📄 Fichier sélectionné:", {
        name: file.name,
        size: formatFileSize(file.size),
        type: file.type,
        timestamp: new Date().toISOString(),
        user: "HiQaody",
      });
    }
  };

  // Gestionnaires de drag & drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      const errors = validateFile(file);

      if (errors.length > 0) {
        alert("Erreur de fichier :\n" + errors.join("\n"));
        return;
      }

      // Créer un événement simulé pour handleFileChange
      const simulatedEvent = {
        target: {
          files: [file],
        },
      };

      setFileInfo({
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: new Date(file.lastModified).toLocaleString("fr-FR"),
      });

      handleFileChange(simulatedEvent);
    }
  };

  return (
    <div className="step-content">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-700">
          Fichiers et matériel de recherche
        </h3>
        <p className="mt-2 text-sm text-gray-600">
          Téléchargez votre document de recherche et ajoutez les informations
          sur le matériel utilisé.
        </p>
      </div>

      {/* Section de téléchargement de fichier améliorée */}
      <div className="mb-8">
        <div className="mb-4 flex items-center justify-between">
          <span className="block text-sm font-medium text-gray-700">
            📎 Document de recherche {<span className="text-red-500">*</span>}
          </span>
          <span className="text-xs text-gray-500">
            Formats acceptés : Word (.doc, .docx), ODT, RTF, TXT
          </span>
        </div>

        <div
          className={`rounded-lg border-2 border-dashed p-6 transition-all duration-200 ${
            dragOver
              ? "border-[#57A863] bg-green-50"
              : "border-gray-300 hover:border-[#57A863] hover:bg-gray-50"
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="text-center">
            <svg
              className={`mx-auto h-12 w-12 transition-colors ${
                dragOver ? "text-[#57A863]" : "text-gray-400"
              }`}
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
              aria-hidden="true"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className="mt-4">
              <label htmlFor="file-upload" className="cursor-pointer">
                <span className="mt-2 block text-sm font-medium text-gray-900">
                  {dragOver
                    ? "Déposez votre fichier ici"
                    : "Cliquez pour télécharger ou glissez-déposez"}
                </span>
                <input
                  id="file-upload"
                  name="file-upload"
                  type="file"
                  className="sr-only"
                  accept=".doc,.docx,.odt,.rtf,.txt,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.oasis.opendocument.text,application/rtf,text/plain"
                  onChange={handleFileChangeEnhanced}
                />
              </label>
              <p className="mt-1 text-xs text-gray-500">
                Documents éditables jusqu'à {formatFileSize(MAX_FILE_SIZE)}
              </p>
            </div>
          </div>
        </div>

        {/* Informations sur le fichier actuel dans l'article */}
        {article?.file_art && (
          <div className="mt-4 rounded-md border border-blue-200 bg-blue-50 p-4">
            <div className="flex items-start">
              <svg
                className="mr-3 h-5 w-5 flex-shrink-0 text-blue-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-800">
                  {getFileIcon(article.file_art)} Fichier actuel :{" "}
                  {article.file_art.split("/").pop()}
                </p>
                <p className="mt-1 text-xs text-blue-600">
                  Ce fichier sera remplacé si vous en téléchargez un nouveau
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Informations sur le nouveau fichier sélectionné */}
        {fileInfo && (
          <div className="mt-4 rounded-md border border-green-200 bg-green-50 p-4">
            <div className="flex items-start">
              <svg
                className="mr-3 h-5 w-5 flex-shrink-0 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="flex-1">
                <p className="text-sm font-medium text-green-800">
                  {getFileIcon(fileInfo.name)} Nouveau fichier sélectionné :{" "}
                  {fileInfo.name}
                </p>
                <div className="mt-2 space-y-1 text-xs text-green-600">
                  <p>• Taille : {formatFileSize(fileInfo.size)}</p>
                  <p>• Type : {fileInfo.type || "Détecté automatiquement"}</p>
                  <p>• Dernière modification : {fileInfo.lastModified}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Section matériel de recherche améliorée */}
      <div className="space-y-6">
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center">
            <svg
              className="mr-2 h-5 w-5 text-[#57A863]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
              />
            </svg>
            <h4 className="text-lg font-medium text-gray-700">
              Matériel et méthodologie de recherche
            </h4>
          </div>

          <div className="space-y-6">
            <div>
              <label
                htmlFor="materiel_rech_art"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Description détaillée du matériel utilisé
              </label>
              <textarea
                id="materiel_rech_art"
                value={formData.materiel_rech_art || ""}
                onChange={(e) =>
                  handleInputChange("materiel_rech_art", null, e.target.value)
                }
                placeholder="Décrivez en détail les matériels, logiciels, données, équipements et méthodologies utilisés..."
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm transition-colors focus:border-[#57A863] focus:outline-none focus:ring-1 focus:ring-[#57A863]"
                rows="6"
              />
            </div>

            <div>
              <label
                htmlFor="research_material_url"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                <span className="flex items-center">
                  <svg
                    className="mr-1 h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                    />
                  </svg>
                  Lien vers les ressources de données
                </span>
              </label>
              <input
                type="url"
                id="research_material_url"
                value={formData.research_material_url || ""}
                onChange={(e) =>
                  handleInputChange(
                    "research_material_url",
                    null,
                    e.target.value
                  )
                }
                placeholder="https://exemple.com/ids ou https://doi.org/... ou https://github.com/..."
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm transition-colors focus:border-[#57A863] focus:outline-none focus:ring-1 focus:ring-[#57A863]"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

StepFiles.propTypes = {
  formData: PropTypes.shape({
    materiel_rech_art: PropTypes.string,
    research_material_url: PropTypes.string,
  }).isRequired,
  handleFileChange: PropTypes.func.isRequired,
  handleInputChange: PropTypes.func.isRequired,
  article: PropTypes.shape({
    file_art: PropTypes.string,
  }),
};

export default StepFiles;
